import React, { useState } from 'react'
import { Card, Input, Button, List, Tag, Space, Typography, Spin, Empty, Row, Col, Statistic } from 'antd'
import { ExperimentOutlined, FileTextOutlined, SearchOutlined, DatabaseOutlined } from '@ant-design/icons'
import { useExtractionTest } from '@/shared/hooks/useKnowledgeQueries'
import type { ExtractionTestResponse } from '@/shared/api/types'
import { useTranslation } from 'react-i18next'
import './ExtractionTest.css'

const { TextArea } = Input
const { Title, Text, Paragraph } = Typography

const ExtractionTest: React.FC = () => {
  const { t, i18n } = useTranslation('modules')
  const currentLanguage = i18n.language
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ExtractionTestResponse | null>(null)

  const extractionTestMutation = useExtractionTest()

  const handleTest = async () => {
    if (!query.trim()) return

    try {
      const response = await extractionTestMutation.mutateAsync({ query: query.trim() })
      setResults(response)
    } catch (error) {
      console.error('抽取测试失败:', error)
    }
  }

  const handleReset = () => {
    setQuery('')
    setResults(null)
  }

  const isLoading = extractionTestMutation.isPending

  return (
    <div className="extraction-test test-card">
      <div className="extraction-header">
        <Title level={4}>{t('knowledge.extractionTest.title')}</Title>
      </div>
      <Row gutter={24} className="test-container">
        {/* 左侧：抽取测试区 (1/3宽度) */}
        <Col span={8} className="test-panel">
          <div className="input-card">
            <div className="panel-header">
              <Title level={5}>{t('knowledge.extractionTest.testQuery')}</Title>
              <Text type="secondary">{t('knowledge.extractionTest.testQueryDescription')}</Text>
            </div>

            <div className="query-section">
              <TextArea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('knowledge.extractionTest.queryPlaceholder')}
                autoSize={{ minRows: 4, maxRows: 8 }}
                className="query-input"
                disabled={isLoading}
              />
            </div>

            <div className="action-section">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  icon={<ExperimentOutlined />}
                  onClick={handleTest}
                  loading={isLoading}
                  disabled={!query.trim()}
                  block
                  size="large"
                >
                  {t('knowledge.extractionTest.startTest')}
                </Button>
                <Button onClick={handleReset} disabled={isLoading} block>
                  {t('knowledge.extractionTest.resetQuery')}
                </Button>
              </Space>
            </div>

            {results && (
              <div className="stats-section">
                <div className="stats-card">
                  <Statistic
                    title={t('knowledge.extractionTest.matchedResults')}
                    value={results.count}
                    prefix={<SearchOutlined />}
                  />
                  <Statistic
                    title={t('knowledge.extractionTest.knowledgeDocuments')}
                    value={results.documents_in_db}
                    prefix={<DatabaseOutlined />}
                  />
                  <Statistic
                    title={t('knowledge.extractionTest.searchParamK')}
                    value={results.search_k}
                  />
                </div>
              </div>
            )}
          </div>
        </Col>

        {/* 右侧：结果展示区 (2/3宽度) */}
        <Col span={16} className="results-panel">
          <div className="results-card">
            <div className="results-header">
              <Title level={5}>{t('knowledge.extractionTest.results')}</Title>
              {results && (
                <Text type="secondary">
                  {t('knowledge.extractionTest.querySummary', { query: results.query, count: results.count })}
                </Text>
              )}
            </div>

            <div className="results-content">
              {isLoading ? (
                <Spin spinning={true} size="large">
                  <div className="loading-container" />
                </Spin>
              ) : results ? (
                <>
                  {results.count === 0 ? (
                    <Empty
                      description={
                        <div>
                          <div>{t('knowledge.extractionTest.noResults')}</div>
                          <Text type="secondary">
                            {t('knowledge.extractionTest.noResultsDescription')}
                          </Text>
                        </div>
                      }
                    />
                  ) : (
                    <div className="results-list">
                      <List
                        style={{ height: '100%' }}
                        dataSource={[...results.results].sort((a, b) => (b.similarity_score || 0) - (a.similarity_score || 0))}
                        renderItem={(item, index) => (
                          <List.Item className="result-item">
                            <div className="result-card">
                              <div className="result-header">
                                <Space align="center">
                                  <Tag color="blue">#{index + 1}</Tag>
                                  <Tag color="green">
                                    {t('knowledge.extractionTest.similarityScore', { score: ((item.similarity_score || 0) * 100).toFixed(1) })}
                                  </Tag>
                                  <Tag color="purple">
                                    <FileTextOutlined /> {t('knowledge.extractionTest.source', { source: item.source })}
                                  </Tag>
                                  {item.metadata?.chunk_index !== undefined && (
                                    <Tag color="orange">{t('knowledge.extractionTest.chunk', { index: item.metadata.chunk_index })}</Tag>
                                  )}
                                </Space>
                              </div>
                              <div className="result-content">
                                <Paragraph
                                  ellipsis={{
                                    rows: 3,
                                    expandable: true,
                                    symbol: (expanded) => expanded ? t('knowledge.extractionTest.collapse') : t('knowledge.extractionTest.expand')
                                  }}
                                >
                                  {item.content}
                                </Paragraph>
                              </div>
                              {item.metadata && (
                                <div className="result-metadata">
                                  <Text type="secondary" className="metadata-text">
                                    {t('knowledge.extractionTest.metadata', {
                                      document: item.metadata.document_name || t('knowledge.extractionTest.unknown'),
                                      size: item.metadata.size || 'N/A',
                                      created: item.metadata.created_at ?
                                        new Date(item.metadata.created_at).toLocaleDateString(currentLanguage) : 'N/A'
                                    })}
                                  </Text>
                                </div>
                              )}
                            </div>
                          </List.Item>
                        )}
                      />
                    </div>
                  )}
                </>
              ) : (
                <Empty
                  description={
                    <div>
                      <div>{t('knowledge.extractionTest.waitingInput')}</div>
                      <Text type="secondary">
                        {t('knowledge.extractionTest.waitingInputDescription')}
                      </Text>
                    </div>
                  }
                />
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default ExtractionTest